import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { GetPageantAwardStore } from "SessionState/Pageant/PageantAwardStore";
import { getAgeGroupName } from "helpers/PageantHelper";
import CsLineIcons from "cs-line-icons/CsLineIcons";

type AwardRelationship = {
  id?: number;
  pageantAvailableTitleId?: number;
  registeredPageantContestantId?: number;
  isEntered?: boolean;
  isWon?: boolean;
};

type JudgeSideAwardWinnersProps = {
  pageantId: number;
  divisionId: number;
  ageGroup: any;
  contestants: any[];
  sideAwards: any[];
  onBack?: () => void;
  onSummaryChange?: (summary: {
    total: number;
    completed: number;
    incomplete: number;
    percent: number;
  }) => void;
  onSavingChange?: (isSaving: boolean) => void;
  onWinnerChange?: (params: {
    registeredPageantContestantId: number;
    pageantAvailableTitleId: number;
    relationship: AwardRelationship;
  }) => Promise<AwardRelationship | null | void>;
};

const getContestantId = (contestant: any): number => {
  return Number(
    contestant?.id ??
      contestant?.registeredContestantId ??
      contestant?.registeredPageantContestantId ??
      0,
  );
};

const getContestantShortName = (contestant: any): string => {
  const firstName = String(
    contestant?.firstName ?? contestant?.contestantName?.split(" ")?.[0] ?? "",
  ).trim();

  const lastName = String(
    contestant?.lastName ??
      contestant?.contestantName?.split(" ")?.slice(1).join(" ") ??
      "",
  ).trim();

  return (
    `${firstName} ${
      lastName ? `${lastName.charAt(0).toUpperCase()}.` : ""
    }`.trim() || "Contestant"
  );
};

const contestantNumberAsNum = (contestant: any): number => {
  const parsed = Number(contestant?.contestantNumber);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const JudgeSideAwardWinners: React.FC<JudgeSideAwardWinnersProps> = ({
  ageGroup,
  contestants,
  sideAwards,
  onSummaryChange,
  onSavingChange,
  onWinnerChange,
}) => {
  const awardStore = GetPageantAwardStore();

  const [relationships, setRelationships] = useState<AwardRelationship[]>([]);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  const displayAwards = sideAwards ?? [];

  const ageGroupLabel =
    getAgeGroupName(ageGroup) ||
    ageGroup?.ageGroupName ||
    ageGroup?.name ||
    "Age Group";

  const relKey = (awardId: number, contestantId: number): string =>
    `${awardId}__${contestantId}`;

  const sortedContestants = useMemo(() => {
    return [...(contestants ?? [])].sort(
      (a, b) => contestantNumberAsNum(a) - contestantNumberAsNum(b),
    );
  }, [contestants]);

  const currentContestantIds = useMemo(() => {
    return new Set(sortedContestants.map((c: any) => getContestantId(c)));
  }, [sortedContestants]);

  useEffect(() => {
    const nestedRelationships = (displayAwards ?? []).flatMap((award: any) =>
      (award?.awardRelationships ?? [])
        .map((relationship: any) => ({
          ...relationship,
          pageantAvailableTitleId:
            relationship?.pageantAvailableTitleId ?? award?.id,
        }))
        .filter((relationship: any) =>
          currentContestantIds.has(
            Number(relationship?.registeredPageantContestantId ?? 0),
          ),
        ),
    );

    setRelationships(nestedRelationships);
  }, [displayAwards, currentContestantIds]);

  useEffect(() => {
    onSavingChange?.(pendingKeys.size > 0);
  }, [pendingKeys.size, onSavingChange]);

  const relationshipByPair = useMemo(() => {
    const map = new Map<string, AwardRelationship>();

    relationships.forEach((rel) => {
      const awardId = Number(rel?.pageantAvailableTitleId ?? 0);
      const contestantId = Number(rel?.registeredPageantContestantId ?? 0);

      if (!awardId || !contestantId) return;

      map.set(relKey(awardId, contestantId), rel);
    });

    return map;
  }, [relationships]);

  const winnersByAward = useMemo(() => {
    const map = new Map<number, Set<number>>();

    relationships.forEach((rel) => {
      const contestantId = Number(rel?.registeredPageantContestantId ?? 0);

      if (!currentContestantIds.has(contestantId)) return;
      if (rel?.isWon !== true) return;

      const awardId = Number(rel?.pageantAvailableTitleId ?? 0);

      if (!awardId || !contestantId) return;

      const existing = map.get(awardId) ?? new Set<number>();
      existing.add(contestantId);
      map.set(awardId, existing);
    });

    return map;
  }, [relationships, currentContestantIds]);

  const summary = useMemo(() => {
    const total = displayAwards.length;

    const winnerCount = relationships.filter((relationship: any) => {
      const contestantId = Number(
        relationship?.registeredPageantContestantId ?? 0,
      );

      return (
        relationship?.isWon === true &&
        contestantId > 0 &&
        currentContestantIds.has(contestantId)
      );
    }).length;

    const awardsWithWinnerCount = displayAwards.filter((award: any) => {
      const awardId = Number(award?.id ?? 0);

      return relationships.some((relationship: any) => {
        const contestantId = Number(
          relationship?.registeredPageantContestantId ?? 0,
        );

        return (
          Number(relationship?.pageantAvailableTitleId ?? 0) === awardId &&
          relationship?.isWon === true &&
          contestantId > 0 &&
          currentContestantIds.has(contestantId)
        );
      });
    }).length;

    return {
      total,
      completed: winnerCount,
      incomplete: Math.max(0, total - awardsWithWinnerCount),
      percent:
        total === 0 ? 0 : Math.round((awardsWithWinnerCount / total) * 100),
    };
  }, [displayAwards, relationships, currentContestantIds]);

  useEffect(() => {
    onSummaryChange?.(summary);
  }, [
    summary.total,
    summary.completed,
    summary.incomplete,
    summary.percent,
    onSummaryChange,
  ]);

  const getRelationshipForAwardContestant = (
    awardId: number,
    contestantId: number,
  ): AwardRelationship | undefined => {
    return relationshipByPair.get(relKey(awardId, contestantId));
  };

  const patchRelationship = (
    awardId: number,
    contestantId: number,
    fallbackPatch: Partial<AwardRelationship>,
    updatedRelationship?: AwardRelationship | null | void,
  ) => {
    setRelationships((prev) =>
      prev.map((rel) => {
        const matches =
          Number(rel?.registeredPageantContestantId ?? 0) === contestantId &&
          Number(rel?.pageantAvailableTitleId ?? 0) === awardId;

        if (!matches) return rel;

        return {
          ...rel,
          ...fallbackPatch,
          ...(updatedRelationship ?? {}),
          pageantAvailableTitleId: awardId,
          registeredPageantContestantId: contestantId,
        };
      }),
    );
  };

  const getAwardScoringCriteria = (award: any): any => {
  if (award?.specificAwardFullCriteria?.scoringCriteria) {
    return award.specificAwardFullCriteria.scoringCriteria;
  }

  if (award?.awardScoringCriteria) {
    try {
      return JSON.parse(String(award.awardScoringCriteria));
    } catch {
      return null;
    }
  }

  return null;
};

  const getOptionClassName = (
    selected: boolean,
    entered: boolean,
    pending: boolean,
  ): string => {
    const base =
      "btn btn-sm d-flex flex-column align-items-center justify-content-center border rounded-3 px-2 py-2";
    const border = entered ? "border-info border-2" : "border-muted";
    const background = selected
      ? entered
        ? "btn-success"
        : "btn-warning"
      : "btn-background text-muted";
    const disabled = pending ? "opacity-50" : "";

    return [base, border, background, disabled].join(" ");
  };

  const toggleWinner = async (awardId: number, contestantId: number) => {
    const key = relKey(awardId, contestantId);

    if (pendingKeys.has(key)) return;

    const relationship = relationshipByPair.get(key);

    if (!relationship?.id) {
      toast.error("No award relationship exists for this contestant.");
      return;
    }

    setPendingKeys((prev) => new Set(prev).add(key));

    try {
      const nextIsWon = !relationship.isWon;

      const updatedRelationship = relationship.isWon
        ? await (awardStore as any).removeWinner?.(relationship.id)
        : await (awardStore as any).confirmWinner?.(relationship.id);

      const normalizedUpdatedRelationship: AwardRelationship = {
        ...relationship,
        ...updatedRelationship,
        isWon: updatedRelationship?.isWon ?? nextIsWon,
        pageantAvailableTitleId: awardId,
        registeredPageantContestantId: contestantId,
      };

      await onWinnerChange?.({
        registeredPageantContestantId: contestantId,
        pageantAvailableTitleId: awardId,
        relationship: normalizedUpdatedRelationship,
      });

      patchRelationship(
        awardId,
        contestantId,
        { isWon: nextIsWon },
        normalizedUpdatedRelationship,
      );
    } catch (error) {
      console.error("Failed to update winner", error);
      toast.error("Failed to update winner.");
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <Card className="shadow-sm border-0">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex flex-wrap gap-3 align-items-center mb-3 small">
          <span>
            <Badge bg="outline-info">Blue border</Badge> = Entered
          </span>
          <span>
            <Badge bg="muted">Gray border</Badge> = Not entered
          </span>
          <span>
            <Badge bg="success">Green fill</Badge> = Winner
          </span>
          <span>
            <Badge bg="warning" text="dark">
              Yellow fill
            </Badge>{" "}
            = Winner but not entered
          </span>
        </div>

        {displayAwards.length === 0 ? (
          <div className="text-center text-muted p-4">
            No side awards available for {ageGroupLabel}.
          </div>
        ) : (
          <>
            <div className="d-none d-md-block table-responsive">
              <table className="table table-sm table-bordered align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 180 }}>Side Award</th>
                    <th>Contestants</th>
                  </tr>
                </thead>

                <tbody>
                  {displayAwards.map((award: any) => {
                    const awardId = Number(award.id);
                    const winnerIds =
                      winnersByAward.get(awardId) ?? new Set<number>();

const hasCriteria = Boolean(award?.awardScoringCriteria);


                    return (
                      <tr key={awardId} className={hasCriteria ? "table-secondary bg-muted" : ""}>
                        <td className="fw-bold">
                          <div className="cta-3">{award.name}</div>
                           {hasCriteria ? (
    <em className="small mt-1">Not Selected By Judges</em>
  ) : null}
                          <div className="mt-1">
                            {winnerIds.size > 0 ? (
                              <Badge bg="success">
                                {winnerIds.size} Winner
                                {winnerIds.size === 1 ? "" : "s"}
                              </Badge>
                            ) : (
                              <Badge bg="secondary">Open</Badge>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {sortedContestants.map((contestant: any) => {
                              const contestantId = getContestantId(contestant);
                              const relationship =
                                getRelationshipForAwardContestant(
                                  awardId,
                                  contestantId,
                                );

                              const entered = Boolean(relationship?.isEntered);
                              const selected = Boolean(relationship?.isWon);
                              const pending = pendingKeys.has(
                                relKey(awardId, contestantId),
                              );

                              return (
                                <button
                                  key={`${awardId}-${contestantId}`}
                                  type="button"
                                  disabled={pending}
                                  className={getOptionClassName(
                                    selected,
                                    entered,
                                    pending,
                                  )}
                                  style={{
                                    minWidth: 76,
                                    maxWidth: 86,
                                    minHeight: 58,
                                    cursor: pending
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                  onClick={() =>
                                    toggleWinner(awardId, contestantId)
                                  }
                                  title={`${getContestantShortName(
                                    contestant,
                                  )} (#${contestant.contestantNumber})`}
                                >
                                  {pending ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <>
                                      <div className="fw-bold lh-1 fs-3">
                                        #
                                        {contestant.contestantNumber ||
                                          contestantId}
                                      </div>

                                      <div
                                        className="small text-truncate w-100"
                                        style={{ maxWidth: 72 }}
                                      >
                                        {getContestantShortName(contestant)}
                                        {selected && (
                                          <CsLineIcons icon="trophy" />
                                        )}
                                      </div>
                                    </>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-md-none d-grid gap-3">
              {displayAwards.map((award: any) => {
                const awardId = Number(award.id);
                const winnerIds =
                  winnersByAward.get(awardId) ?? new Set<number>();

                return (
                  <Card key={awardId} className="border shadow-sm">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="fw-bold">{award.name}</div>

                        {winnerIds.size > 0 ? (
                          <Badge bg="success">
                            {winnerIds.size} Winner
                            {winnerIds.size === 1 ? "" : "s"}
                          </Badge>
                        ) : (
                          <Badge bg="secondary">Open</Badge>
                        )}
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        {sortedContestants.map((contestant: any) => {
                          const contestantId = getContestantId(contestant);
                          const relationship =
                            getRelationshipForAwardContestant(
                              awardId,
                              contestantId,
                            );

                          const entered = Boolean(relationship?.isEntered);
                          const selected = Boolean(relationship?.isWon);
                          const pending = pendingKeys.has(
                            relKey(awardId, contestantId),
                          );

                          return (
                            <button
                              key={`${awardId}-${contestantId}`}
                              type="button"
                              disabled={pending}
                              className={getOptionClassName(
                                selected,
                                entered,
                                pending,
                              )}
                              style={{
                                flex: "1 0 calc(33.333% - 0.5rem)",
                                minWidth: 82,
                                minHeight: 58,
                                cursor: pending ? "not-allowed" : "pointer",
                              }}
                              onClick={() =>
                                toggleWinner(awardId, contestantId)
                              }
                            >
                              {pending ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <div className="fw-bold lh-1 fs-4">
                                    #
                                    {contestant.contestantNumber ||
                                      contestantId}
                                  </div>

                                  <div
                                    className="small text-truncate w-100"
                                    style={{ maxWidth: 72 }}
                                  >
                                    {getContestantShortName(contestant)}
                                    {selected && (
                                      <CsLineIcons icon="trophy" />
                                    )}
                                  </div>
                                </>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};
export default JudgeSideAwardWinners;